/**
 * CountdownToClose
 *
 * Pill component showing days remaining until an account's target close date.
 *
 * Color bands:
 *   null close date   → grey  "No close date"
 *   overdue (< 0)     → red   "Overdue by N days"
 *   0–7 days          → red   "Closes in N days"
 *   8–30 days         → amber "Closes in N days"
 *   31–90 days        → green "Closes in N days"
 *   > 90 days         → blue  "Closes in N days"
 *
 * Stale overlay (requires lastActivity):
 *   > 30 days no activity → " · stale (hard)" in red
 *   14–30 days            → " · stale (soft)" in amber
 *
 * Modes:
 *   compact=false (default) — pill + date line below
 *   compact=true            — inline pill only, for table rows
 */

interface CountdownToCloseProps {
  targetCloseDate: string | null | undefined;
  lastActivity?: number; // account.updatedAt (ms since epoch)
  compact?: boolean;
}

type Band = "none" | "red" | "amber" | "green" | "blue";
type StaleLevel = "hard" | "soft" | null;

function computeDaysToClose(targetCloseDate: string): number {
  // Parse as local date (YYYY-MM-DD) to avoid TZ offset issues
  const [y, m, d] = targetCloseDate.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = target.getTime() - today.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function computeStale(lastActivity: number | undefined): StaleLevel {
  if (lastActivity === undefined || lastActivity === 0) return null;
  const daysSince = Math.floor((Date.now() - lastActivity) / (1000 * 60 * 60 * 24));
  if (daysSince > 30) return "hard";
  if (daysSince >= 14) return "soft";
  return null;
}

function getBand(daysToClose: number): Band {
  if (daysToClose < 0) return "red";
  if (daysToClose <= 7) return "red";
  if (daysToClose <= 30) return "amber";
  if (daysToClose <= 90) return "green";
  return "blue";
}

// Light-background styles per band
const BAND_STYLES: Record<Band | "none", { bg: string; text: string; border: string }> = {
  none:  { bg: "#F3F4F6", text: "#6B7280", border: "#E5E7EB" },
  red:   { bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA" },
  amber: { bg: "#FFFBEB", text: "#92400E", border: "#FDE68A" },
  green: { bg: "#F0FDF4", text: "#166534", border: "#BBF7D0" },
  blue:  { bg: "#FAFAF7", text: "#0B0B0F", border: "#E5E5E7" },
};

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function CountdownToClose({
  targetCloseDate,
  lastActivity,
  compact = false,
}: CountdownToCloseProps) {
  // --- No close date ---
  // D-006: muted gray — informational only, not an error state.
  if (!targetCloseDate) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          backgroundColor: "#F3F4F6",
          color: "#9CA3AF",
          border: "1px dashed #D1D5DB",
          borderRadius: 9999,
          padding: compact ? "2px 8px" : "3px 10px",
          fontSize: compact ? 11 : 12,
          fontWeight: 500,
          letterSpacing: "0.01em",
          whiteSpace: "nowrap",
        }}
        title="No target close date set. Add one in the account scorecard."
      >
        No close date
      </span>
    );
  }

  const days = computeDaysToClose(targetCloseDate);
  const band = getBand(days);
  const stale = computeStale(lastActivity);
  const s = BAND_STYLES[band];

  // Label
  let label: string;
  if (days < 0) {
    label = `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`;
  } else if (days === 0) {
    label = "Closes today";
  } else {
    label = `Closes in ${days} day${days === 1 ? "" : "s"}`;
  }

  // Urgency indicator for 0-7 days (small dot)
  const showUrgency = days >= 0 && days <= 7;

  const pillStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    backgroundColor: s.bg,
    color: s.text,
    border: `1px solid ${s.border}`,
    borderRadius: 9999,
    padding: compact ? "2px 8px" : "3px 10px",
    fontSize: compact ? 11 : 12,
    fontWeight: 500,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
  };

  const pill = (
    <span style={pillStyle}>
      {showUrgency && (
        <span
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: s.text,
            opacity: 0.7,
            flexShrink: 0,
          }}
        />
      )}
      {label}
      {stale === "hard" && (
        <span style={{ color: "#B91C1C", fontWeight: 400, marginLeft: 2 }}>
          {" · stale (hard)"}
        </span>
      )}
      {stale === "soft" && (
        <span style={{ color: "#92400E", fontWeight: 400, marginLeft: 2 }}>
          {" · stale (soft)"}
        </span>
      )}
    </span>
  );

  if (compact) {
    return pill;
  }

  // Full mode: pill + formatted date below
  return (
    <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
      {pill}
      <span
        style={{
          fontSize: 11,
          color: "#9CA3AF",
          paddingLeft: 2,
          letterSpacing: "0.01em",
        }}
      >
        {formatDate(targetCloseDate)}
      </span>
    </div>
  );
}
